import { Accordion, Flex, Paper, Text, Title } from "@mantine/core";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis } from "recharts";
import { useMediaQuery } from "@mantine/hooks";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import useReportStore from "../../../store/useReportStore";

const generateRadarData = (
  strengths: {
    subject: string;
    value: number;
    color: string;
    description: string;
    index: number;
  }[]
) => {
  return strengths.map((item) => ({
    subject: item.subject,
    subjectMobile: `${item.index + 1}`,
    A: item.value,
    fullMark: 100,
  }));
};

export const StrengthsDerivedFromResume = () => {
  const matches = useMediaQuery("(max-width: 1100px)");

  const CustomPolarTick = ({ payload, cx, cy, textAnchor }: any) => {
    const RADIAN = Math.PI / 180;
    const angle = payload.coordinate;
    const radius = matches ? 120 : 190;
    const radiusX = matches ? 120 : 210;

    const x = cx + radiusX * Math.cos(-angle * RADIAN);
    const y = cy + radius * Math.sin(-angle * RADIAN);

    const words = payload.value.split(" ");

    return (
      <text
        x={x}
        y={y - 10}
        textAnchor="middle"
        fill="#000"
        fontSize={12}
        fontWeight="bold"
      >
        {words.map((word: string, index: number) => (
          <tspan x={x} dy={index === 0 ? 0 : 14} key={index}>
            {word}
          </tspan>
        ))}
      </text>
    );
  };

  const { report, reportId } = useReportStore();

  const strengthsData = report?.strengths || [];

  const strengths = strengthsData.data.map((strength, index) => ({
    index,
    subject: strength.title,
    value: strength.percentage,
    color:
      index % 5 === 0
        ? "#05D8D2"
        : index % 5 === 1
        ? "#3A8BD9"
        : index % 5 === 2
        ? "#6F40E2"
        : index % 5 === 3
        ? "#A64DD7"
        : "#DE57CC",
    description: strength.description,
    title: strength.title,
  }));

  const radarData = generateRadarData(strengths);

  if (strengthsData.data.length === 0) {
    return (
      <Paper radius="24px" p="32px" mt={32} withBorder>
        <Title size={36} mb={8}>
          Loading strengths data...
        </Title>
        <Text c="dimmed">Please wait while we fetch your strengths data.</Text>
      </Paper>
    );
  }

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title size={matches ? 24 : 36} mb={8}>
        {strengthsData?.mark_down?.replace("##", "")}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {strengthsData?.explanation}
      </Text>
      <Flex
        justify="space-between"
        align="center"
        direction={matches ? "column" : undefined}
      >
        <RadarChart
          cx="50%"
          cy="50%"
          outerRadius="80%"
          width={matches ? 279 : 596}
          height={matches ? 296 : 422}
          data={radarData}
        >
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#05D8D2" stopOpacity={1} />
              <stop offset="50%" stopColor="#6F40E2" stopOpacity={1} />
              <stop offset="100%" stopColor="#DE57CC" stopOpacity={1} />
            </linearGradient>
          </defs>
          <PolarGrid />
          <PolarAngleAxis
            dataKey={matches ? "subjectMobile" : "subject"}
            tick={<CustomPolarTick />}
          />
          {/* <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} /> */}
          <Radar
            name="Strengths"
            dataKey="A"
            stroke="#8884d8"
            fill="url(#colorUv)"
            fillOpacity={0.6}
            dot={{ stroke: "#6F40E2", fill: "#6F40E2", strokeWidth: 2, r: 3 }}
          />
        </RadarChart>

        <Accordion w={matches ? "100%" : "50%"}>
          {strengths.map((strength, index) => (
            <Accordion.Item value={strength.title} key={index}>
              <Accordion.Control>
                <Flex justify="space-between">
                  <Text fw="bold">{`${index + 1}. ${strength.title}`}</Text>
                  <Text fw="bold" c={strength.color} mr={20}>
                    {strength.value}%
                  </Text>
                </Flex>
              </Accordion.Control>
              <Accordion.Panel>
                <Text>{strength.description}</Text>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Flex>
      <LikeResponse report_id={reportId} section="strengths" />
    </Paper>
  );
};
